import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Chip, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert, TextField, Select, MenuItem, FormControl, InputLabel, Pagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PageLayout from '../components/common/PageLayout';
import { get, post } from '../utils/api';

const PROVIDER_LABEL = { authnet: 'Authorize.Net', stripe: 'Stripe' };
const TYPE_LABEL = { ach_return: 'ACH return', refunded: 'Refund', refund: 'Refund', voided: 'Void', subscription_failure: 'Failed charge' };
const sevColor = (s) => (s === 'critical' ? 'error' : s === 'warning' ? 'warning' : 'default');
const statusColor = (s) => (s === 'new' ? 'warning' : s === 'open' ? 'info' : 'success');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US') : '—');
const isoDay = (d) => d.toISOString().slice(0, 10);
const deliveryText = (r) => (r.recipients === '(baseline)' ? 'baselined' : r.emailed_at ? `emailed ${fmtDate(r.emailed_at)}` : 'not sent');

export default function PaymentAlertsPage() {
  const [log, setLog] = useState({ rows: [], pending: 0, recipients: [] });
  const [preview, setPreview] = useState({ events: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [provider, setProvider] = useState('all');
  const [type, setType] = useState('all');
  const [source, setSource] = useState('all');
  const [status, setStatusFilter] = useState('active'); // active (new+open) | new | open | closed | all
  const [from, setFrom] = useState(isoDay(new Date(Date.now() - 30 * 86400000)));
  const [to, setTo] = useState(isoDay(new Date()));
  const [page, setPage] = useState(1);
  const PER = 25;

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [l, p] = await Promise.all([get('/api/manage/alerts'), get('/api/manage/alerts/preview')]);
      setLog(l || { rows: [], pending: 0, recipients: [] });
      setPreview(p || { events: [] });
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to load alerts'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [provider, type, source, status, search, from, to]);

  const setRowStatus = async (id, s) => {
    setLog((prev) => ({ ...prev, rows: prev.rows.map((r) => (r.id === id ? { ...r, status: s } : r)) }));
    try { await post('/api/manage/alerts/status', { ids: [id], status: s }); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Could not update status'); load(); }
  };

  const newRows = (preview.events || []).map((e, i) => ({ ...e, id: null, _logged: false, status: 'new', _key: `new:${e.provider}:${e.event_type}:${e.ref || i}` }));
  const logRows = (log.rows || []).map((r) => ({ ...r, _logged: true, _key: `log:${r.id}` }));
  const all = [...newRows, ...logRows];

  const dFrom = from ? new Date(`${from}T00:00:00`) : null;
  const dTo = to ? new Date(`${to}T23:59:59`) : null;
  const sq = search.trim().toLowerCase();
  const typeMatch = (r) => (type === 'all' ? true : type === 'refund' ? (r.event_type === 'refunded' || r.event_type === 'refund') : r.event_type === type);
  const statusMatch = (r) => (status === 'active' ? r.status !== 'closed' : status === 'all' ? true : r.status === status);
  const dateMatch = (r) => { if (!r.event_at) return true; const t = new Date(r.event_at); return (!dFrom || t >= dFrom) && (!dTo || t <= dTo); };
  const rows = all.filter((r) => (provider === 'all' || r.provider === provider)
    && typeMatch(r) && (source === 'all' || r.source_kind === source) && statusMatch(r) && dateMatch(r)
    && (!sq || `${r.donor_name || ''} ${r.summary || ''} ${r.source_label || ''} ${r.account || ''} ${r.event_type || ''}`.toLowerCase().includes(sq)));

  const pageCount = Math.max(1, Math.ceil(rows.length / PER));
  const pageSafe = Math.min(page, pageCount);
  const paged = rows.slice((pageSafe - 1) * PER, (pageSafe - 1) * PER + PER);

  const sel = (label, value, onChange, opts) => (
    <FormControl size="small" sx={{ minWidth: 130 }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        {opts.map((o) => <MenuItem key={o[0]} value={o[0]}>{o[1]}</MenuItem>)}
      </Select>
    </FormControl>
  );

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>Payments</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Payment Alerts</Typography>
          <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Returns, refunds, voids and failed charges across Authorize.Net + Stripe, from the nightly reconciliation sweeps.
          A digest emails automatically each night to: <strong>{(log.recipients || []).join(', ') || '—'}</strong>.
        </Typography>

        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}

        <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search name, email, description…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240, flex: '1 1 240px' }} />
          {sel('Status', status, setStatusFilter, [['active', 'New + open'], ['new', 'New'], ['open', 'Open'], ['closed', 'Closed'], ['all', 'All']])}
          {sel('Provider', provider, setProvider, [['all', 'All providers'], ['authnet', 'Authorize.Net'], ['stripe', 'Stripe']])}
          {sel('Type', type, setType, [['all', 'All types'], ['ach_return', 'ACH returns'], ['refund', 'Refunds'], ['voided', 'Voids'], ['subscription_failure', 'Failed charges']])}
          {sel('Source', source, setSource, [['all', 'All sources'], ['subscription', 'Subscription'], ['one_time', 'One-time']])}
          <TextField size="small" label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
          <TextField size="small" label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
        </Paper>

        <Paper variant="outlined">
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          ) : rows.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>No alerts for this filter.</Box>
          ) : (
            <>
              <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{rows.length} events · {PER} per page</Typography>
                <Pagination count={pageCount} page={pageSafe} onChange={(e, p) => setPage(p)} size="small" color="primary" siblingCount={0} boundaryCount={1} />
              </Box>
              <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', '& td, & th': { px: 1, verticalAlign: 'top', wordBreak: 'break-word' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '8%' }}>Severity</TableCell><TableCell sx={{ width: '8%' }}>Date</TableCell>
                    <TableCell sx={{ width: '11%' }}>Provider / type</TableCell><TableCell sx={{ width: '39%' }}>Event</TableCell>
                    <TableCell sx={{ width: '16%' }}>Source</TableCell><TableCell sx={{ width: '18%' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((r) => (
                    <TableRow key={r._key} hover>
                      <TableCell><Chip size="small" color={sevColor(r.severity)} variant={r.severity === 'critical' ? 'filled' : 'outlined'} label={r.severity} /></TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.event_at)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{PROVIDER_LABEL[r.provider] || r.provider}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{TYPE_LABEL[r.event_type] || r.event_type}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2">{r.summary}</Typography></TableCell>
                      <TableCell>
                        {r.source_kind === 'subscription' ? (
                          <>
                            <Chip size="small" color="primary" variant="outlined" label="Subscription" />
                            {r.source_label && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>{r.source_label}</Typography>}
                          </>
                        ) : r.source_kind === 'one_time' ? <Chip size="small" variant="outlined" label="One-time" />
                          : <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>}
                      </TableCell>
                      <TableCell>
                        {r._logged ? (
                          <FormControl size="small" fullWidth>
                            <Select value={r.status || 'new'} onChange={(e) => setRowStatus(r.id, e.target.value)}
                              sx={{ '& .MuiSelect-select': { py: 0.5 }, color: `${statusColor(r.status)}.dark` }}>
                              <MenuItem value="new">New</MenuItem>
                              <MenuItem value="open">Open</MenuItem>
                              <MenuItem value="closed">Closed</MenuItem>
                            </Select>
                          </FormControl>
                        ) : <Chip size="small" color="warning" variant="outlined" label="New · unsent" />}
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>{r._logged ? deliveryText(r) : 'awaiting next run'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Paper>
      </Box>
    </PageLayout>
  );
}
